import React, { useState, useEffect, useContext, useCallback } from 'react';
import { servicesAPI } from '../../api/services';
import { formatCurrency } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import './ServiceManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'all',      label: 'All Services', icon: '🧺', filter: null },
  { id: 'wash',     label: 'Wash',         icon: '💧', filter: 'wash' },
  { id: 'dry_clean',label: 'Dry Clean',    icon: '✨', filter: 'dry_clean' },
  { id: 'iron',     label: 'Iron',         icon: '♨️', filter: 'iron' },
  { id: 'wash_iron',label: 'Wash & Iron',  icon: '🫧', filter: 'wash_iron' },
  { id: 'inactive', label: 'Inactive',     icon: '🚫', filter: '__inactive__' },
];

const TYPE_META = {
  wash:      { label: 'Wash',        bg: '#dbeafe', color: '#1e40af', icon: '💧' },
  dry_clean: { label: 'Dry Clean',   bg: '#ede9fe', color: '#4c1d95', icon: '✨' },
  iron:      { label: 'Iron',        bg: '#fef3c7', color: '#92400e', icon: '♨️' },
  wash_iron: { label: 'Wash & Iron', bg: '#d1fae5', color: '#065f46', icon: '🫧' },
};

const UNIT_LABELS = {
  piece: 'Piece',
  kg:    'Kilogram',
  item:  'Item',
};

const SERVICE_TYPES = [
  { value: 'wash',      label: 'Wash' },
  { value: 'dry_clean', label: 'Dry Clean' },
  { value: 'iron',      label: 'Iron' },
  { value: 'wash_iron', label: 'Wash & Iron' },
];

const UNIT_OPTIONS = [
  { value: 'piece', label: 'Piece' },
  { value: 'kg',    label: 'Kilogram' },
  { value: 'item',  label: 'Item' },
];

const buildEmptyForm = () => ({
  name:          '',
  type:          'wash',
  price:         '',
  unit:          'piece',
  taxPercentage: '0',
  estimatedTime: '',
  description:   '',
  isActive:      true,
});

// ─── Inline sub-components ────────────────────────────────────────────────────

const TypeBadge = ({ type }) => {
  const m = TYPE_META[type] || { label: type, bg: '#f1f5f9', color: '#475569' };
  return (
    <span className="sm-badge" style={{ background: m.bg, color: m.color }}>
      {m.icon} {m.label}
    </span>
  );
};

const StatusBadge = ({ active }) => (
  <span className="sm-badge" style={{
    background: active ? '#d1fae5' : '#fee2e2',
    color:      active ? '#065f46' : '#991b1b',
  }}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Service Detail Content ───────────────────────────────────────────────────

const ServiceDetailContent = ({ service, onEdit, formatCurrency }) => {
  const tm  = TYPE_META[service.type] || {};
  const basePrice = parseFloat(service.price) || 0;
  const taxAmt    = basePrice * ((parseFloat(service.taxPercentage) || 0) / 100);
  const totalPrice = basePrice + taxAmt;

  return (
    <div className="sm-detail">

      {/* Top bar: badges + actions */}
      <div className="sm-detail-top-bar">
        <div className="sm-detail-badges">
          <TypeBadge type={service.type} />
          <StatusBadge active={service.isActive} />
          <span className="sm-badge" style={{ background: '#f1f5f9', color: '#475569' }}>
            {UNIT_LABELS[service.unit] || service.unit}
          </span>
        </div>
        <button className="sm-icon-btn" onClick={onEdit}>✏️ Edit Service</button>
      </div>

      {/* Two-col: service info + pricing */}
      <div className="sm-detail-2col">

        {/* Service Info */}
        <div className="sm-detail-section">
          <div className="sm-section-title">Service Information</div>
          {[
            ['Service Name',    service.name],
            ['Type',            `${tm.icon || ''} ${tm.label || service.type}`],
            ['Unit',            UNIT_LABELS[service.unit] || service.unit],
            ['Estimated Time',  `${service.estimatedTime} hr${service.estimatedTime !== 1 ? 's' : ''}`],
            ['Tax Rate',        `${service.taxPercentage ?? 0}%`],
            ['Created',         service.createdAt ? new Date(service.createdAt).toLocaleDateString('en-KW') : '—'],
            ['Last Updated',    service.updatedAt ? new Date(service.updatedAt).toLocaleDateString('en-KW') : '—'],
          ].map(([label, val]) => (
            <div key={label} className="sm-info-row">
              <span className="sm-info-label">{label}</span>
              <span className="sm-info-val">{val || '—'}</span>
            </div>
          ))}
          {service.description && (
            <div className="sm-desc-box">
              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Description
              </span>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#374151' }}>{service.description}</p>
            </div>
          )}
        </div>

        {/* Pricing Breakdown */}
        <div className="sm-detail-section">
          <div className="sm-section-title">Pricing Breakdown</div>
          <div className="sm-price-card">
            <div className="sm-price-row">
              <span>Base Price</span>
              <span style={{ fontWeight: 700 }}>{formatCurrency(basePrice)}</span>
            </div>
            <div className="sm-price-row">
              <span>Tax ({service.taxPercentage ?? 0}%)</span>
              <span style={{ color: '#64748b' }}>+ {formatCurrency(taxAmt)}</span>
            </div>
            <div className="sm-price-divider" />
            <div className="sm-price-total-row">
              <span>Price per {UNIT_LABELS[service.unit] || service.unit}</span>
              <span>{formatCurrency(totalPrice)}</span>
            </div>
            <div className="sm-price-final-row">
              <span>Effective Price</span>
              <span>{formatCurrency(totalPrice)}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

// ─── Service Form ─────────────────────────────────────────────────────────────

const ServiceFormContent = ({ formData, setFormData, formErrors, setFormErrors }) => {
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <div className="ps-modal-form" style={{ paddingTop: 0 }}>

      {/* Name */}
      <div style={{ marginBottom: 14 }}>
        <div className="sm-form-field">
          <label className="sm-form-label">
            Service Name <span className="req">*</span>
          </label>
          <input
            className={`sm-form-input${formErrors.name ? ' error' : ''}`}
            value={formData.name}
            placeholder="e.g. Shirt Wash & Iron"
            onChange={e => handleChange('name', e.target.value)}
          />
          {formErrors.name && <div className="sm-field-error">{formErrors.name}</div>}
        </div>
      </div>

      {/* Type + Unit */}
      <div className="sm-form-grid-2">
        <div className="sm-form-field">
          <label className="sm-form-label">Type <span className="req">*</span></label>
          <select
            className="sm-form-select"
            value={formData.type}
            onChange={e => handleChange('type', e.target.value)}
          >
            {SERVICE_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="sm-form-field">
          <label className="sm-form-label">Unit <span className="req">*</span></label>
          <select
            className="sm-form-select"
            value={formData.unit}
            onChange={e => handleChange('unit', e.target.value)}
          >
            {UNIT_OPTIONS.map(u => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Price + Tax + Estimated Time */}
      <div className="sm-form-grid-3">
        <div className="sm-form-field">
          <label className="sm-form-label">Price (KD) <span className="req">*</span></label>
          <input
            type="number" step="0.001" min="0"
            className={`sm-form-input${formErrors.price ? ' error' : ''}`}
            value={formData.price}
            placeholder="0.000"
            onChange={e => handleChange('price', e.target.value)}
          />
          {formErrors.price && <div className="sm-field-error">{formErrors.price}</div>}
        </div>
        <div className="sm-form-field">
          <label className="sm-form-label">Tax %</label>
          <input
            type="number" step="0.01" min="0" max="100"
            className="sm-form-input"
            value={formData.taxPercentage}
            placeholder="0"
            onChange={e => handleChange('taxPercentage', e.target.value)}
          />
        </div>
        <div className="sm-form-field">
          <label className="sm-form-label">Est. Time (hrs) <span className="req">*</span></label>
          <input
            type="number" min="1"
            className={`sm-form-input${formErrors.estimatedTime ? ' error' : ''}`}
            value={formData.estimatedTime}
            placeholder="e.g. 24"
            onChange={e => handleChange('estimatedTime', e.target.value)}
          />
          {formErrors.estimatedTime && <div className="sm-field-error">{formErrors.estimatedTime}</div>}
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: 14 }}>
        <div className="sm-form-field">
          <label className="sm-form-label">Description</label>
          <textarea
            className="sm-form-textarea"
            value={formData.description}
            placeholder="Optional: describe what's included in this service…"
            onChange={e => handleChange('description', e.target.value)}
            rows={3}
          />
        </div>
      </div>

      {/* Active toggle */}
      <div className="sm-toggle-row">
        <button
          type="button"
          className={`sm-toggle-switch${formData.isActive ? ' on' : ' off'}`}
          onClick={() => handleChange('isActive', !formData.isActive)}
        >
          <div className="sm-toggle-knob" />
        </button>
        <span className="sm-toggle-label">
          {formData.isActive ? '✅ Service is Active' : '🚫 Service is Inactive'}
        </span>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ServiceManagement = () => {
  const { showNotification } = useContext(NotificationContext);

  // ── Tab + list state ─────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]   = useState('all');
  const [services, setServices]     = useState([]);
  const [allServices, setAllServices] = useState([]); // unfiltered for summary counts
  const [loading, setLoading]       = useState(false);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [search, setSearch]           = useState('');
  const [filterUnit, setFilterUnit]   = useState('');

  // ── Detail modal ─────────────────────────────────────────────────────────────
  const [selectedService, setSelectedService] = useState(null);
  const [detailLoading, setDetailLoading]     = useState(false);

  // ── Create / Edit modal ──────────────────────────────────────────────────────
  const [showFormModal, setShowFormModal]   = useState(false);
  const [formMode, setFormMode]             = useState('create');
  const [editingId, setEditingId]           = useState(null);
  const [formData, setFormData]             = useState(buildEmptyForm());
  const [formErrors, setFormErrors]         = useState({});
  const [formLoading, setFormLoading]       = useState(false);

  // ── Delete modal ─────────────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [deleteLoading, setDeleteLoading]     = useState(false);

  // ── Load all services (for summary counts) ───────────────────────────────────
  const loadAllServices = useCallback(async () => {
    try {
      const res = await servicesAPI.getAll();
      setAllServices(res.data || []);
    } catch { /* non-critical */ }
  }, []);

  // ── Load filtered services ───────────────────────────────────────────────────
  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      const tab = TABS.find(t => t.id === activeTab);
      if (tab?.filter === '__inactive__') {
        params.isActive = false;
      } else if (tab?.filter) {
        params.type     = tab.filter;
        params.isActive = true;
      }
      if (filterUnit) params.unit = filterUnit;

      const res = await servicesAPI.getAll(params);
      let list = res.data || [];

      // Client-side search
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter(s =>
          s.name?.toLowerCase().includes(q) ||
          s.description?.toLowerCase().includes(q) ||
          s.type?.toLowerCase().includes(q)
        );
      }
      setServices(list);
    } catch {
      showNotification('Failed to load services', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, search, filterUnit]);

  useEffect(() => { loadAllServices(); }, []);
  useEffect(() => { loadServices(); }, [activeTab, search, filterUnit]);

  // ── Summary counts ───────────────────────────────────────────────────────────
  const tabCount = (filter) => {
    if (!filter || filter === '__inactive__') {
      return filter === '__inactive__'
        ? allServices.filter(s => !s.isActive).length
        : allServices.length;
    }
    return allServices.filter(s => s.type === filter && s.isActive).length;
  };

  const hasFilters = !!filterUnit;
  const clearFilters = () => { setFilterUnit(''); };

  // ── Open detail ──────────────────────────────────────────────────────────────
  const openDetail = async (service) => {
    setSelectedService(service);
    setDetailLoading(true);
    try {
      const res = await servicesAPI.getById(service._id);
      setSelectedService(res.data);
    } catch {
      showNotification('Failed to load service details', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Open create modal ────────────────────────────────────────────────────────
  const openCreate = () => {
    setFormData(buildEmptyForm());
    setFormErrors({});
    setFormMode('create');
    setEditingId(null);
    setShowFormModal(true);
  };

  // ── Open edit modal ──────────────────────────────────────────────────────────
  const openEdit = (service) => {
    setFormData({
      name:          service.name,
      type:          service.type,
      price:         String(service.price),
      unit:          service.unit,
      taxPercentage: String(service.taxPercentage ?? 0),
      estimatedTime: String(service.estimatedTime),
      description:   service.description || '',
      isActive:      service.isActive ?? true,
    });
    setFormErrors({});
    setFormMode('edit');
    setEditingId(service._id);
    setShowFormModal(true);
  };

  // ── Validate form ────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.name.trim())                          errs.name = 'Service name is required';
    if (!formData.price || parseFloat(formData.price) <= 0)
                                                         errs.price = 'Valid price is required';
    if (!formData.estimatedTime || parseInt(formData.estimatedTime) < 1)
                                                         errs.estimatedTime = 'Valid estimated time is required';
    return errs;
  };

  // ── Save service ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    setFormLoading(true);
    try {
      const payload = {
        name:          formData.name.trim(),
        type:          formData.type,
        price:         parseFloat(formData.price),
        unit:          formData.unit,
        taxPercentage: parseFloat(formData.taxPercentage) || 0,
        estimatedTime: parseInt(formData.estimatedTime),
        description:   formData.description.trim(),
        isActive:      formData.isActive,
      };

      if (formMode === 'create') {
        const res = await servicesAPI.create(payload);
        showNotification(`Service "${res.data.name}" created`, 'success');
      } else {
        const res = await servicesAPI.update(editingId, payload);
        // Refresh detail if editing what's open
        if (selectedService?._id === editingId) {
          setSelectedService(res.data);
        }
        showNotification(`Service "${res.data.name}" updated`, 'success');
      }
      setShowFormModal(false);
      loadServices();
      loadAllServices();
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to save service', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // ── Deactivate (soft delete) ─────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await servicesAPI.delete(deleteTarget._id);
      showNotification(`Service "${deleteTarget.name}" deactivated`, 'success');
      if (selectedService?._id === deleteTarget._id) setSelectedService(null);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadServices();
      loadAllServices();
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to deactivate service', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="ps-root">

      {/* ── Header ── */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Services</h1>
          <p className="ps-subtitle">Manage laundry services, pricing and tax rates</p>
        </div>
        <Button variant="primary" onClick={openCreate}>+ New Service</Button>
      </div>

      {/* ── Summary Cards ── */}
      <div className="ps-summary-row">
        <SummaryCard
          icon="🧺" label="Total Services" accent="#3b82f6"
          value={allServices.filter(s => s.isActive).length}
          sub={`${allServices.length} including inactive`}
        />
        <SummaryCard
          icon="💧" label="Wash" accent="#1e40af"
          value={allServices.filter(s => s.type === 'wash' && s.isActive).length}
          sub="active wash services"
        />
        <SummaryCard
          icon="✨" label="Dry Clean" accent="#4c1d95"
          value={allServices.filter(s => s.type === 'dry_clean' && s.isActive).length}
          sub="active dry clean services"
        />
        <SummaryCard
          icon="♨️" label="Iron / Wash & Iron" accent="#92400e"
          value={allServices.filter(s => (s.type === 'iron' || s.type === 'wash_iron') && s.isActive).length}
          sub="ironing services"
        />
        <SummaryCard
          icon="🚫" label="Inactive" accent="#ef4444"
          value={allServices.filter(s => !s.isActive).length}
          sub="deactivated services"
        />
      </div>

      {/* ── Tabs ── */}
      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.id); }}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            <span className="sm-tab-count">
              {tab.id === 'all'
                ? allServices.filter(s => s.isActive).length
                : tab.filter === '__inactive__'
                ? allServices.filter(s => !s.isActive).length
                : allServices.filter(s => s.type === tab.filter && s.isActive).length}
            </span>
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="ps-content">
        <div className="ps-tab-pane">

          {/* Filters */}
          <div className="ps-filters">
            <div className="ps-search-wrap">
              <span className="ps-search-icon">🔍</span>
              <input
                className="ps-search-input"
                placeholder="Search by service name or description…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="sm-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>

            <select
              className="ps-filter-select"
              value={filterUnit}
              onChange={e => setFilterUnit(e.target.value)}
            >
              <option value="">All Units</option>
              {UNIT_OPTIONS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>

            {hasFilters && (
              <button className="sm-clear-filters" onClick={clearFilters}>Clear filters ✕</button>
            )}

            <Button variant="secondary" onClick={() => { loadServices(); loadAllServices(); }}>↺</Button>
          </div>

          {/* Table meta */}
          <div className="ps-table-meta">
            <span className="ps-count">
              {services.length} service{services.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="ps-loading">Loading services…</div>
          ) : (
            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Type</th>
                    <th>Unit</th>
                    <th style={{ textAlign: 'right' }}>Base Price</th>
                    <th style={{ textAlign: 'right' }}>Tax %</th>
                    <th style={{ textAlign: 'right' }}>Effective Price</th>
                    <th>Est. Time</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {services.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="ps-empty">
                        <div style={{ fontSize: 36, marginBottom: 8 }}>🧺</div>
                        No services found
                      </td>
                    </tr>
                  ) : services.map(service => {
                    const tm  = TYPE_META[service.type] || {};
                    const base    = parseFloat(service.price) || 0;
                    const taxAmt  = base * ((parseFloat(service.taxPercentage) || 0) / 100);
                    const effective = base + taxAmt;

                    return (
                      <tr
                        key={service._id}
                        className="ps-row"
                        onClick={() => openDetail(service)}
                      >
                        {/* Service name */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                              className="sm-service-icon"
                              style={{ background: tm.bg || '#f1f5f9' }}
                            >
                              {tm.icon || '🧺'}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                {service.name}
                              </div>
                              {service.description && (
                                <div style={{ fontSize: 11, color: '#94a3b8', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {service.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <td><TypeBadge type={service.type} /></td>

                        {/* Unit */}
                        <td style={{ fontSize: 13, color: '#64748b' }}>
                          {UNIT_LABELS[service.unit] || service.unit}
                        </td>

                        {/* Base price */}
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                          {formatCurrency(base)}
                        </td>

                        {/* Tax % */}
                        <td style={{ textAlign: 'right', color: '#64748b', fontSize: 13 }}>
                          {service.taxPercentage ?? 0}%
                        </td>

                        {/* Effective price */}
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                          {formatCurrency(effective)}
                        </td>

                        {/* Est. time */}
                        <td style={{ color: '#64748b', fontSize: 13 }}>
                          {service.estimatedTime} hr{service.estimatedTime !== 1 ? 's' : ''}
                        </td>

                        {/* Status */}
                        <td><StatusBadge active={service.isActive} /></td>

                        {/* Actions */}
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="sm-action-btn"
                              onClick={e => { e.stopPropagation(); openEdit(service); }}
                              title="Edit service"
                            >
                              ✏️
                            </button>
                            {service.isActive && (
                              <button
                                className="sm-action-btn danger"
                                onClick={e => { e.stopPropagation(); setDeleteTarget(service); setShowDeleteModal(true); }}
                                title="Deactivate service"
                              >
                                🚫
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          SERVICE DETAIL MODAL
          ════════════════════════════════════════════════════════ */}
      {selectedService && (
        <Modal
          isOpen={!!selectedService}
          onClose={() => setSelectedService(null)}
          title={selectedService.name}
          size="large"
        >
          {detailLoading ? (
            <div className="ps-loading">Loading service details…</div>
          ) : (
            <ServiceDetailContent
              service={selectedService}
              onEdit={() => {
                setSelectedService(null);
                openEdit(selectedService);
              }}
              formatCurrency={formatCurrency}
            />
          )}
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════
          CREATE / EDIT MODAL
          ════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={formMode === 'create' ? '+ New Service' : `Edit: ${formData.name || 'Service'}`}
        size="large"
      >
        <ServiceFormContent
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
        />
        <div className="ps-modal-actions" style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <Button variant="secondary" onClick={() => setShowFormModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={formLoading}>
            {formLoading
              ? 'Saving…'
              : formMode === 'create' ? 'Create Service' : 'Save Changes'}
          </Button>
        </div>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          DEACTIVATE CONFIRM MODAL
          ════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        title="Deactivate Service"
      >
        {deleteTarget && (
          <div className="ps-modal-form">
            <div className="sm-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Deactivate "{deleteTarget.name}"?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  This service will be hidden from new orders. Existing orders using this service will not be affected. You can reactivate it later by editing the service.
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deactivating…' : 'Deactivate Service'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ServiceManagement;
